import { Box, Card, Grid, Typography, Paper } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getCustomerRetirementDetailsbyId } from 'service/CustomerService';
import Chart from './Chart';
import { euroSymbol } from 'utils/Constants';
import { strings } from 'i18n/Strings';

interface ILifetimeGoal {
  current_age: number;
  retire_age: number;
  ret_Income: number;
  currentContributionPerMonth: string;
  requiredContributionPerMonth: string;
}

const LifetimeGoal = (): JSX.Element => {
  const { id } = useParams();
  const [lifetimeGoal, setLifetimeGoal] = useState<ILifetimeGoal>({
    current_age: 0,
    retire_age: 0,
    ret_Income: 0,
    currentContributionPerMonth: '',
    requiredContributionPerMonth: '',
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const fetchData = async () => {
      try {
        const retireDetails = (await getCustomerRetirementDetailsbyId(
          String(id),
        )) as { retirementData: ILifetimeGoal };
        setLifetimeGoal(retireDetails.retirementData);
      } catch (error) {
        console.error(error);
      }
    };
    void fetchData();
  }, [id]);

  return (
    <Box display="flex" flexDirection="column">
      <Paper>
        <Grid
          container
          spacing={10}
          justifyContent="space-evenly"
          alignItems="center"
        >
          <Grid item xs={6}>
            {[
              {
                label: strings.displayText.ageOfRetirement,
                value: lifetimeGoal?.retire_age
                  ? `${lifetimeGoal?.retire_age} years`
                  : '-',
              },
              {
                label: strings.displayText.annualIncome,
                value: lifetimeGoal?.ret_Income
                  ? `${
                      euroSymbol.EURO
                    } ${lifetimeGoal?.ret_Income.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : '-',
              },
              {
                label: strings.displayText.currentContribution,
                value: lifetimeGoal?.currentContributionPerMonth
                  ? `${euroSymbol.EURO} ${Number(
                      lifetimeGoal?.currentContributionPerMonth,
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : '-',
              },
              {
                label: strings.displayText.requiredContribution,
                value: lifetimeGoal?.requiredContributionPerMonth
                  ? `${euroSymbol.EURO} ${Number(
                      lifetimeGoal?.requiredContributionPerMonth,
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : '-',
              },
            ].map(({ label, value }) => (
              <Card key={label} elevation={2} sx={{ p: 2, m: 3 }}>
                <Typography
                  variant="h5"
                  fontSize="14px"
                  fontFamily="Inter"
                  color="#898989"
                >
                  {label}
                </Typography>
                <Typography
                  variant="h5"
                  fontFamily="Inter"
                  fontWeight={700}
                  fontSize="20px"
                >
                  {value}
                </Typography>
              </Card>
            ))}
          </Grid>

          <Grid item xs={6} pb={5}>
            {lifetimeGoal?.ret_Income ? (
              <Box display="flex" justifyContent="center">
                <Chart
                  annualIncome={lifetimeGoal?.ret_Income}
                  retireAge={lifetimeGoal?.retire_age}
                  age={lifetimeGoal?.current_age}
                  requiredContributionPerMonth={Number(
                    lifetimeGoal?.requiredContributionPerMonth,
                  )}
                  currentContributionPerMonth={Number(
                    lifetimeGoal?.currentContributionPerMonth,
                  )}
                />
              </Box>
            ) : (
              ''
            )}

            {lifetimeGoal?.retire_age ? (
              <Box
                sx={{ mt: 2, display: 'flex', justifyContent: 'space-around' }}
              >
                {[
                  {
                    label: strings.displayText.targetNumbers,
                    value: `${
                      euroSymbol.EURO
                    } ${lifetimeGoal?.ret_Income.toLocaleString()}`,
                  },
                  {
                    label: strings.displayText.expectedAt65,
                    value: `${euroSymbol.EURO} ${lifetimeGoal?.requiredContributionPerMonth}`,
                  },
                  {
                    label: strings.displayText.currentGap,
                    value: `${euroSymbol.EURO} ${(
                      lifetimeGoal?.ret_Income -
                      Number(lifetimeGoal?.requiredContributionPerMonth)
                    ).toLocaleString()}`,
                  },
                ].map(({ label, value }) => (
                  <Box
                    key={label}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Typography fontFamily="Inter" fontSize="14px">
                      {label}
                    </Typography>
                    <Typography
                      fontFamily="Inter"
                      fontSize="14px"
                      fontWeight={600}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              ''
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default LifetimeGoal;
